import cx from 'classnames';
import { useMemo } from 'react';
import { GENERIC_VEHICLE_IMAGE } from '~/constants/endpoints';
import {
  useGetIsWIPVehicle,
  useGetVehicleDetails
} from '~/context/vdp/VehicleContext';
import type { ImageMap } from '~/hooks/common/ui/useImageWithFallback';

const genericHeroImageClasses = 'block h-full w-full';

export enum ImageType {
  Generic = 'generic',
  Hero = 'hero',
  JellyBean = 'jellyBean'
}

type ReturnProps = {
  heroImageMap: ImageMap<ImageType>;
  initialImageType: ImageType;
};

export default function useGetHeroImages(): ReturnProps {
  const isWIPVehicle = useGetIsWIPVehicle();
  const {
    stockImageData: { jellyBeanDesktopUrl = '' } = {},
    vexVdpImageData: { heroes: { hero = '' } = {} } = {}
  } = useGetVehicleDetails();

  // Image details used on render if the url is loaded
  const heroImageMap: ImageMap<ImageType> = useMemo(
    () => ({
      [ImageType.Hero]: {
        classes: cx(
          genericHeroImageClasses,
          'bg-background-default object-contain'
        ),
        fallbackImageType: ImageType.JellyBean,
        imageType: ImageType.Hero,
        shouldLogFallback: true,
        url: hero
      },
      [ImageType.JellyBean]: {
        classes: cx(
          genericHeroImageClasses,
          'bg-background-weak object-cover md:object-contain'
        ),
        fallbackImageType: ImageType.Generic,
        imageType: ImageType.JellyBean,
        url: jellyBeanDesktopUrl
      },
      [ImageType.Generic]: {
        classes: cx(
          genericHeroImageClasses,
          'bg-background-weak object-cover md:object-contain'
        ),
        imageType: ImageType.Generic,
        url: GENERIC_VEHICLE_IMAGE
      }
    }),
    [hero, jellyBeanDesktopUrl]
  );

  return useMemo(
    () => ({
      heroImageMap,
      initialImageType: isWIPVehicle ? ImageType.JellyBean : ImageType.Hero
    }),
    [heroImageMap, isWIPVehicle]
  );
}
